/* eslint-disable prettier/prettier */
import { FileDescriptor } from '@servicetitan/design-system';
import { injectable } from '@servicetitan/react-ioc';
import axios from 'axios';
import { Validator } from 'formstate';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

export enum UploadStatus {
    UploadStarted,
    NetworkError,
    UploadError,
    FileSizeError,
    FileExtensionsError,
    UploadSuccess,
}

export const maxFileSize = 1024 * 1024 * 15;
export const validExtensions = ['.jpg', '.jpeg', '.png', '.tiff', '.tff', '.webp'];

@injectable()
export class AvatarFileUploadStore {
    @observable isApplyingData = false;
    @observable file?: FileDescriptor;
    @observable uploadStatus?: UploadStatus;
    @observable fileData?: { error: string | null; fileName: string };
    @observable isPopoverOpen = false;
    @observable previewImageUrl?: string;
    @observable isPreviewImageLoading = false;

    constructor() {
        makeObservable(this);
    }

    @action
    init = (photoUrl?: string) => {
        if (photoUrl) {
            this.file = {
                file: { name: photoUrl.split('/').pop() } as File,
            };
        }
    };

    @action
    importData = async (url: string) => {
        this.setStatus(UploadStatus.UploadStarted);
        try {
            if (this.file) {
                const { file } = this.file;
                const formData = new FormData();
                formData.append('file', file);
                const data = await axios.post(url, formData);
                if (data.data) {
                    runInAction(async () => {
                        this.fileData = data.data;
                        this.setStatus(UploadStatus.UploadSuccess);
                    });
                }
            }
        } catch (e: unknown) {
            if (axios.isAxiosError(e) && e.message === 'Network Error') {
                this.setStatus(UploadStatus.NetworkError);
            } else {
                this.setStatus(UploadStatus.UploadError);
            }
        }
    };

    @action
    setStatus = (value?: UploadStatus) => {
        this.uploadStatus = value;
    };

    @action
    setFileFromFileList = (files?: FileList) => {
        if (files) {
            this.file = {
                file: files[0],
            };
        } else {
            this.file = undefined;
        }
    };

    @action
    togglePopover = (val?: boolean) => {
        this.isPopoverOpen = val ?? !this.isPopoverOpen;
    };

    @action
    onFileLoad = async (files: FileList) => {
        this.onDelete();
        this.setStatus(undefined);
        this.setFileFromFileList(files);
        const errorType = this.validateFile(this.file?.file as File);
        if (errorType) {
            this.onDelete();
            this.setStatus(errorType);
            return;
        }
        await this.loadPreviewFile();
    };

    @action
    onFileReplace: (data: { file: FileDescriptor; newFile: File }) => Promise<void> = async ({
        newFile,
    }) => {
        this.setStatus(undefined);
        const errorType = this.validateFile(newFile);
        if (errorType) {
            this.setStatus(errorType);
            return;
        }
        this.onDelete();
        this.file = { file: newFile } as FileDescriptor;
        await this.loadPreviewFile();
    };

    @action
    loadPreviewFile = () => {
        return new Promise<void>(resolve => {
            const reader = new FileReader();
            const file = this.file?.file as File;
            this.isPreviewImageLoading = true;
            reader.onload = e => {
                runInAction(() => {
                    if (e.target?.result) {
                        this.previewImageUrl = e.target?.result as string;
                    }
                });
            };
            reader.onloadend = () => {
                runInAction(() => {
                    this.isPreviewImageLoading = false;
                });
                resolve();
            };
            reader.readAsDataURL(file);
        });
    };

    @action
    onUpload = async (url: string, maxResolution?: number) => {
        const file = this.file!.file as File;
        await this.importData(`${url}?name=${file.name}&maxSize=${maxResolution}`);
        this.togglePopover(false);
        return this.fileData?.fileName;
    };

    @action
    onDelete = () => {
        this.setFileFromFileList();
        this.previewImageUrl = undefined;
        this.fileData = undefined;
        this.uploadStatus = undefined;
        this.isPreviewImageLoading = false;
    };

    get previewImage() {
        return this.fileData?.fileName;
    }

    @computed
    get error() {
        if (!this.uploadStatus) {
            return;
        }
        switch (this.uploadStatus) {
            case UploadStatus.FileSizeError:
                return 'File size must be smaller than 15mb.';
            case UploadStatus.FileExtensionsError:
                return `Allowed file types: ${validExtensions.join(', ')}`;
            case UploadStatus.NetworkError:
                return 'A network error occurred. Please try again.';
            case UploadStatus.UploadError:
                return 'There was an issue uploading your file. Please try again.';
        }
    }

    @computed
    get isLoading() {
        return this.uploadStatus === UploadStatus.UploadStarted;
    }

    private validateFile = (file: File) => {
        return (
            (fileSizeValidator(maxFileSize)(file) && UploadStatus.FileSizeError) ||
            (this.fileExtensionsValidator(file) && UploadStatus.FileExtensionsError)
        );
    };

    private fileExtensionsValidator = (value: File | undefined) => {
        if (value === undefined) {
            return false;
        }
        const fileExtension = '.' + value.name.split('.').pop()?.toLowerCase();
        return !validExtensions.includes(fileExtension);
    };
}

export const fileSizeValidator =
    (maxSize: number, errorMessage?: string): Validator<File | undefined> =>
    (value: File | undefined) => {
        if (value === undefined) {
            return null;
            }
            if (value.size > maxSize) {
                if (errorMessage) {
                    return errorMessage;
                }
                return `File size too large.`;
            }
            return null;
        };
